import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import { toast } from 'react-toastify'
import * as types from '../../../actions/index.types'
import * as req from '../../../../axios/request/SocietyPortal/ShiftReqs/ShiftReqs'

// The shift API returns two different error shapes.
//
// A ShiftError carries a machine-readable code inside the envelope's data
// ({ success:false, message, data:{ code, ... } }) — branch on that, never on
// message text. validationMiddleware answers earlier in the chain with its own
// body ({ status:'error', message }) that has no success and no data, so `code`
// is simply null for those and the message is already field-specific.
const errorInfo = (e) => {
    const envelope = e?.response?.data
    return {
        message: envelope?.message || e?.message || 'Something went wrong',
        code: envelope?.data?.code || null,
        // Several codes carry extra fields the recovery dialogs need:
        // SHIFT_IN_USE → assignments, ASSIGNMENT_OVERLAP →
        // conflicting_assignment, SHIFT_CODE_TAKEN → shift_id.
        data: envelope?.data || null,
    }
}

// A 2xx body with success:false — same shape for the caller as a rejection.
const bodyError = (result, fallback) => ({
    message: result?.message || fallback,
    code: result?.data?.code || null,
    data: result?.data || null,
})

// Shared worker for every mutating call: dispatch, toast, hand the result (or
// the error) back to the screen. `silentCodes` are the ones a screen handles
// itself with a recovery dialog, where a red toast would just be noise.
function* mutate(action, requestFn, successType, failureType, options = {}) {
    const { successMessage, failureMessage = 'Something went wrong', silentCodes = [] } = options
    try {
        const result = yield call(requestFn, action)
        if (result?.success === true) {
            yield put({ type: successType, payload: result.data })
            toast.success(result?.message || successMessage)
            action.data?.onSuccess?.(result.data)
        } else {
            const info = bodyError(result, failureMessage)
            yield put({ type: failureType, error: info })
            if (!silentCodes.includes(info.code)) toast.error(info.message)
            action.data?.onError?.(info)
        }
    } catch (e) {
        const info = errorInfo(e)
        yield put({ type: failureType, error: info })
        if (!silentCodes.includes(info.code)) toast.error(info.message)
        action.data?.onError?.(info)
    }
}

// Shared worker for every list/read call.
function* fetchList(action, requestFn, successType, failureType, options = {}) {
    const { failureMessage = 'Something went wrong', toPayload = (r) => r.data, silent = false } = options
    try {
        const result = yield call(requestFn, action)
        if (result?.success === true) {
            yield put({ type: successType, payload: toPayload(result) })
            action?.data?.onSuccess?.(result.data, result.meta)
        } else {
            yield put({ type: failureType, error: bodyError(result, failureMessage) })
            if (!silent) toast.error(result?.message || failureMessage)
        }
    } catch (e) {
        const info = errorInfo(e)
        yield put({ type: failureType, error: info })
        if (!silent) toast.error(info.message)
    }
}

// ---- Shift definitions ----------------------------------------------------

function* getShiftEnums(action) {
    // Enums only feed dropdowns — fail quietly, the screens have fallbacks.
    yield* fetchList(action, req.getShiftEnumsReq,
        types.SOCIETY_GET_SHIFT_ENUMS_SUCCESS, types.SOCIETY_GET_SHIFT_ENUMS_FAILURE,
        { silent: true })
}

function* getShifts(action) {
    yield* fetchList(action, req.getShiftsReq,
        types.SOCIETY_GET_SHIFTS_SUCCESS, types.SOCIETY_GET_SHIFTS_FAILURE,
        {
            failureMessage: 'Failed to fetch shifts',
            toPayload: (r) => ({ rows: r.data || [], pagination: r.meta?.pagination }),
        })
}

function* getShiftDetails(action) {
    yield* fetchList(action, req.getShiftByIdReq,
        types.SOCIETY_GET_SHIFT_DETAILS_SUCCESS, types.SOCIETY_GET_SHIFT_DETAILS_FAILURE,
        { failureMessage: 'Failed to fetch the shift' })
}

function* createShift(action) {
    // SHIFT_CODE_TAKEN carries the existing shift_id — the form offers to open
    // it instead of just reporting a clash.
    yield* mutate(action, req.createShiftReq,
        types.SOCIETY_CREATE_SHIFT_SUCCESS, types.SOCIETY_CREATE_SHIFT_FAILURE,
        { successMessage: 'Shift created', failureMessage: 'Failed to create the shift', silentCodes: ['SHIFT_CODE_TAKEN'] })
}

function* updateShift(action) {
    yield* mutate(action, req.updateShiftReq,
        types.SOCIETY_UPDATE_SHIFT_SUCCESS, types.SOCIETY_UPDATE_SHIFT_FAILURE,
        { successMessage: 'Shift saved', failureMessage: 'Failed to save the shift', silentCodes: ['SHIFT_CODE_TAKEN'] })
}

function* deleteShift(action) {
    // SHIFT_IN_USE is the common answer and is not really a failure — the
    // screen turns it into a "deactivate instead" dialog with the count.
    yield* mutate(action, req.deleteShiftReq,
        types.SOCIETY_DELETE_SHIFT_SUCCESS, types.SOCIETY_DELETE_SHIFT_FAILURE,
        { successMessage: 'Shift deleted', failureMessage: 'Failed to delete the shift', silentCodes: ['SHIFT_IN_USE'] })
}

// ---- Assignments ----------------------------------------------------------

function* getShiftAssignments(action) {
    yield* fetchList(action, req.getShiftAssignmentsReq,
        types.SOCIETY_GET_SHIFT_ASSIGNMENTS_SUCCESS, types.SOCIETY_GET_SHIFT_ASSIGNMENTS_FAILURE,
        {
            failureMessage: 'Failed to fetch shift assignments',
            toPayload: (r) => ({ rows: r.data || [], pagination: r.meta?.pagination }),
        })
}

function* getShiftAssignmentDetails(action) {
    yield* fetchList(action, req.getShiftAssignmentByIdReq,
        types.SOCIETY_GET_SHIFT_ASSIGNMENT_DETAILS_SUCCESS, types.SOCIETY_GET_SHIFT_ASSIGNMENT_DETAILS_FAILURE,
        { failureMessage: 'Failed to fetch the assignment' })
}

function* createShiftAssignment(action) {
    // ASSIGNMENT_OVERLAP carries the conflicting assignment so the screen can
    // pre-fill "end that one on <date> and retry" — and an open-ended
    // assignment overlaps everything after its start, so this 409 is the
    // normal answer for an employee who already has a shift.
    yield* mutate(action, req.createShiftAssignmentReq,
        types.SOCIETY_CREATE_SHIFT_ASSIGNMENT_SUCCESS, types.SOCIETY_CREATE_SHIFT_ASSIGNMENT_FAILURE,
        {
            successMessage: 'Shift assigned',
            failureMessage: 'Failed to assign the shift',
            silentCodes: ['ASSIGNMENT_OVERLAP'],
        })
}

function* updateShiftAssignment(action) {
    yield* mutate(action, req.updateShiftAssignmentReq,
        types.SOCIETY_UPDATE_SHIFT_ASSIGNMENT_SUCCESS, types.SOCIETY_UPDATE_SHIFT_ASSIGNMENT_FAILURE,
        {
            successMessage: 'Assignment updated',
            failureMessage: 'Failed to update the assignment',
            silentCodes: ['ASSIGNMENT_OVERLAP'],
        })
}

function* endShiftAssignment(action) {
    yield* mutate(action, req.endShiftAssignmentReq,
        types.SOCIETY_END_SHIFT_ASSIGNMENT_SUCCESS, types.SOCIETY_END_SHIFT_ASSIGNMENT_FAILURE,
        { successMessage: 'Assignment ended', failureMessage: 'Failed to end the assignment' })
}

function* deleteShiftAssignment(action) {
    yield* mutate(action, req.deleteShiftAssignmentReq,
        types.SOCIETY_DELETE_SHIFT_ASSIGNMENT_SUCCESS, types.SOCIETY_DELETE_SHIFT_ASSIGNMENT_FAILURE,
        { successMessage: 'Assignment deleted', failureMessage: 'Failed to delete the assignment' })
}

export default function* ShiftSaga() {
    yield takeLatest(types.SOCIETY_GET_SHIFT_ENUMS, getShiftEnums)
    yield takeLatest(types.SOCIETY_GET_SHIFTS, getShifts)
    yield takeLatest(types.SOCIETY_GET_SHIFT_DETAILS, getShiftDetails)
    yield takeEvery(types.SOCIETY_CREATE_SHIFT, createShift)
    yield takeEvery(types.SOCIETY_UPDATE_SHIFT, updateShift)
    yield takeEvery(types.SOCIETY_DELETE_SHIFT, deleteShift)

    yield takeLatest(types.SOCIETY_GET_SHIFT_ASSIGNMENTS, getShiftAssignments)
    yield takeLatest(types.SOCIETY_GET_SHIFT_ASSIGNMENT_DETAILS, getShiftAssignmentDetails)
    // takeEvery on the assignment writes: "change shift" fires an /end and a
    // create in sequence, and takeLatest would cancel the first one's response.
    yield takeEvery(types.SOCIETY_CREATE_SHIFT_ASSIGNMENT, createShiftAssignment)
    yield takeEvery(types.SOCIETY_UPDATE_SHIFT_ASSIGNMENT, updateShiftAssignment)
    yield takeEvery(types.SOCIETY_END_SHIFT_ASSIGNMENT, endShiftAssignment)
    yield takeEvery(types.SOCIETY_DELETE_SHIFT_ASSIGNMENT, deleteShiftAssignment)
}
